/**
 * Inbound receive dock — pure helpers and draft types.
 * Shell: InboundReceivePage.tsx. API: inboundApi.ts.
 */
import {
  formatNumber,
  type CountMethod,
  type InboundDetailResponse,
  type InboundLineRow,
  type InboundReceipt,
  type InboundReceiptInput,
  type InboundReceiptItemInput,
  type InboundSourceType,
  type IqcStatus,
} from "./inboundApi";
import { inboundReceiptMatchesArrivalPackage } from "./inboundReceiveReceipt";
import { iqcQuantityFieldsFromLine } from "./InboundIqcSessionModal";
import {
  buildInboundLineMpnDraftFromLine,
  emptyInboundLineMpnDraftFields,
  INBOUND_UNKNOWN_PART_LABEL_SKU,
} from "./inboundReceiveMpn";
import {
  canonicalInboundLineForSku,
  inboundLineIdsForSku,
} from "./inboundReceiveLineSort";
import {
  closedQuantityForLine,
  closedQuantityForLineSku,
  type ClosedSubcontainerItem,
} from "./inboundReceiveSubcontainer";

export const RECEIVE_PAGE_HOVER_HINT =
  "Operator receiving on the dock kiosk. Scan an ARRIVED carton tag, tap a line, and key quantity on the numpad. "
  + "Pick bag, box, reel, tray, or another container type for each sealed put-away unit, then seal and print its 4x2 inventory label. "
  + "Unseal a sealed container to move counts back to the open container and edit or remove lines. "
  + "Optional SKU barcode scan when parts are labeled. "
  + "Capture packing slips with the camera before you finish the arrival crate. Save each page separately when the slip is multiple pages. "
  + "Tap the printer icon in the header to change the ZPL queue (same choice as Arrivals and the receive list).";

export function looksLikeArrivalPackageScan(raw: string): boolean {
  const compact = raw.trim().replace(/[\s-]/g, "").toLowerCase();
  return /^[0-9a-f]{32}$/u.test(compact);
}

export interface LineDraft {
  received_quantity: string;
  count_method: CountMethod;
  condition_notes: string;
  iqc_status: IqcStatus;
  iqc_notes: string;
  iqc_passed_quantity: string;
  iqc_failed_quantity: string;
  iqc_skipped_quantity: string;
  scanned_mpn: string;
  mpn_matched: boolean;
  unknown_mpn_confirmed: boolean;
  unknown_mpn_quantity: string;
  approved_component_id: string | null;
  manufacturer_name: string;
}

export const EMPTY_LINE_DRAFT: LineDraft = {
  received_quantity: "",
  count_method: "manual",
  condition_notes: "",
  iqc_status: "pending",
  iqc_notes: "",
  iqc_passed_quantity: "",
  iqc_failed_quantity: "",
  iqc_skipped_quantity: "",
  ...emptyInboundLineMpnDraftFields(),
};

export function isInboundSourceType(value: string | undefined): value is InboundSourceType {
  return value === "purchase_order" || value === "transfer_order";
}

export function countedQuantity(draft: LineDraft | undefined): number {
  if (draft == null) return 0;
  const parsed = Number.parseFloat(draft.received_quantity);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function unknownCountedQuantity(draft: LineDraft | undefined): number {
  if (draft == null) return 0;
  const parsed = Number.parseFloat(draft.unknown_mpn_quantity);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function buildInitialDrafts(lines: InboundLineRow[]): Record<string, LineDraft> {
  const drafts: Record<string, LineDraft> = {};
  for (const line of lines) {
    const quantities = iqcQuantityFieldsFromLine({
      passed: line.iqc_passed_quantity,
      failed: line.iqc_failed_quantity,
      skipped: line.iqc_skipped_quantity,
    });
    drafts[line.source_item_id] = {
      received_quantity: line.draft_received_quantity ?? "",
      count_method: line.count_method ?? "manual",
      condition_notes: line.condition_notes ?? "",
      iqc_status: line.iqc_status ?? "pending",
      iqc_notes: line.iqc_notes ?? "",
      ...quantities,
      ...buildInboundLineMpnDraftFromLine(line),
      unknown_mpn_quantity: line.unknown_mpn_quantity ?? "",
    };
  }
  return drafts;
}

export function parseOptionalIqcQuantity(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

export function isOrderLine(line: InboundLineRow): boolean {
  return line.line_kind == null || line.line_kind === "order_line";
}

export function draftHasMpnFields(
  draft:
    | Pick<
        LineDraft,
        "scanned_mpn" | "mpn_matched" | "unknown_mpn_confirmed" | "approved_component_id" | "manufacturer_name"
      >
    | undefined,
): boolean {
  if (draft == null) return false;
  return (
    draft.scanned_mpn.trim() !== ""
    || draft.mpn_matched
    || draft.unknown_mpn_confirmed
    || (draft.approved_component_id != null && draft.approved_component_id.trim() !== "")
  );
}

export function resolveInboundOrderLineMpnDraft(
  line: InboundLineRow,
  lines: readonly InboundLineRow[],
  drafts: Record<string, LineDraft>,
): Pick<
  LineDraft,
  "scanned_mpn" | "mpn_matched" | "unknown_mpn_confirmed" | "approved_component_id" | "manufacturer_name"
> {
  const lineIds = isOrderLine(line) ? inboundLineIdsForSku(line.sku, lines) : [line.source_item_id];
  const canonicalId = lineIds[0] ?? line.source_item_id;
  const canonicalDraft = drafts[canonicalId];
  if (draftHasMpnFields(canonicalDraft)) {
    return {
      scanned_mpn: canonicalDraft.scanned_mpn,
      mpn_matched: canonicalDraft.mpn_matched,
      unknown_mpn_confirmed: canonicalDraft.unknown_mpn_confirmed,
      approved_component_id: canonicalDraft.approved_component_id,
      manufacturer_name: canonicalDraft.manufacturer_name,
    };
  }
  const ownDraft = drafts[line.source_item_id];
  if (draftHasMpnFields(ownDraft)) {
    return {
      scanned_mpn: ownDraft.scanned_mpn,
      mpn_matched: ownDraft.mpn_matched,
      unknown_mpn_confirmed: ownDraft.unknown_mpn_confirmed,
      approved_component_id: ownDraft.approved_component_id,
      manufacturer_name: ownDraft.manufacturer_name,
    };
  }
  for (let index = lineIds.length - 1; index >= 0; index -= 1) {
    const siblingDraft = drafts[lineIds[index]!];
    if (draftHasMpnFields(siblingDraft)) {
      return {
        scanned_mpn: siblingDraft.scanned_mpn,
        mpn_matched: siblingDraft.mpn_matched,
        unknown_mpn_confirmed: siblingDraft.unknown_mpn_confirmed,
        approved_component_id: siblingDraft.approved_component_id,
        manufacturer_name: siblingDraft.manufacturer_name,
      };
    }
  }
  const fromOwnLine = buildInboundLineMpnDraftFromLine(line);
  if (draftHasMpnFields(fromOwnLine)) {
    return {
      scanned_mpn: fromOwnLine.scanned_mpn,
      mpn_matched: fromOwnLine.mpn_matched,
      unknown_mpn_confirmed: fromOwnLine.unknown_mpn_confirmed,
      approved_component_id: fromOwnLine.approved_component_id,
      manufacturer_name: fromOwnLine.manufacturer_name,
    };
  }
  const byId = new Map(lines.map((row) => [row.source_item_id, row]));
  for (let index = lineIds.length - 1; index >= 0; index -= 1) {
    const siblingLine = byId.get(lineIds[index]!);
    if (siblingLine == null) continue;
    const fromSiblingLine = buildInboundLineMpnDraftFromLine(siblingLine);
    if (draftHasMpnFields(fromSiblingLine)) {
      return {
        scanned_mpn: fromSiblingLine.scanned_mpn,
        mpn_matched: fromSiblingLine.mpn_matched,
        unknown_mpn_confirmed: fromSiblingLine.unknown_mpn_confirmed,
        approved_component_id: fromSiblingLine.approved_component_id,
        manufacturer_name: fromSiblingLine.manufacturer_name,
      };
    }
  }
  return {
    scanned_mpn: "",
    mpn_matched: false,
    unknown_mpn_confirmed: false,
    approved_component_id: null,
    manufacturer_name: "",
  };
}

/** Merge duplicate PO line drafts onto the canonical line for that SKU. */
export function consolidateInboundDuplicateSkuDrafts(
  lines: readonly InboundLineRow[],
  drafts: Record<string, LineDraft>,
): Record<string, LineDraft> {
  const next: Record<string, LineDraft> = { ...drafts };
  const seenSkus = new Set<string>();
  for (const line of lines) {
    if (!isOrderLine(line)) continue;
    const key = line.sku.trim().toLowerCase();
    if (seenSkus.has(key)) continue;
    seenSkus.add(key);
    const lineIds = inboundLineIdsForSku(line.sku, lines);
    if (lineIds.length <= 1) continue;
    const canonicalLine = canonicalInboundLineForSku(line.sku, lines);
    const canonicalId = canonicalLine?.source_item_id;
    if (canonicalLine == null || canonicalId == null) continue;
    let receivedTotal = 0;
    let unknownTotal = 0;
    for (const lineId of lineIds) {
      const draft = next[lineId];
      if (draft == null) continue;
      receivedTotal += countedQuantity(draft);
      unknownTotal += unknownCountedQuantity(draft);
    }
    const mpnFields = resolveInboundOrderLineMpnDraft(canonicalLine, lines, next);
    const canonicalDraft = {
      ...(next[canonicalId] ?? EMPTY_LINE_DRAFT),
      ...mpnFields,
    };
    if (receivedTotal > 0) {
      canonicalDraft.received_quantity = String(receivedTotal);
    }
    if (unknownTotal > 0) {
      canonicalDraft.unknown_mpn_quantity = String(unknownTotal);
    }
    next[canonicalId] = canonicalDraft;
    for (const lineId of lineIds) {
      if (lineId === canonicalId) continue;
      const siblingDraft = next[lineId];
      if (siblingDraft == null) continue;
      next[lineId] = {
        ...siblingDraft,
        received_quantity: "",
        unknown_mpn_quantity: "",
      };
    }
  }
  return next;
}

/** Skip untouched order lines on autosave so large POs do not POST every row each numpad commit. */
export function inboundLineDraftNeedsReceiptPersist(
  line: Pick<InboundLineRow, "receipt_item_id">,
  draft: LineDraft,
): boolean {
  if (line.receipt_item_id != null && line.receipt_item_id.trim() !== "") return true;
  if (countedQuantity(draft) > 0) return true;
  if (unknownCountedQuantity(draft) > 0) return true;
  if (draft.scanned_mpn.trim() !== "") return true;
  if (draft.unknown_mpn_confirmed) return true;
  if (draft.mpn_matched) return true;
  if (draft.approved_component_id != null && draft.approved_component_id.trim() !== "") return true;
  if (draft.condition_notes.trim() !== "") return true;
  if (draft.iqc_notes.trim() !== "") return true;
  if (draft.iqc_status !== "pending") return true;
  if (parseOptionalIqcQuantity(draft.iqc_passed_quantity) != null) return true;
  if (parseOptionalIqcQuantity(draft.iqc_failed_quantity) != null) return true;
  if (parseOptionalIqcQuantity(draft.iqc_skipped_quantity) != null) return true;
  return false;
}

/** Line counts and receipt item ids from GET detail after a save (including joined crate drafts). */
export function draftsFromInboundDetailLines(lines: InboundLineRow[]): Record<string, LineDraft> {
  return consolidateInboundDuplicateSkuDrafts(lines, buildInitialDrafts(lines));
}

/**
 * Keep the just-saved local count when a post-save detail read is stale or
 * misses the package-scoped receipt item. A later refetch may advance the
 * server count, but it must never make the operator's successful entry vanish.
 */
export function reconcileInboundDraftsAfterSave(
  lines: InboundLineRow[],
  savedDrafts: Record<string, LineDraft>,
): Record<string, LineDraft> {
  const serverDrafts = draftsFromInboundDetailLines(lines);
  const localDrafts = consolidateInboundDuplicateSkuDrafts(lines, savedDrafts);
  const lineIdsBySku = new Map<string, string[]>();

  for (const line of lines) {
    if (!isOrderLine(line)) continue;
    const sku = line.sku.trim().toLowerCase();
    lineIdsBySku.set(sku, [...(lineIdsBySku.get(sku) ?? []), line.source_item_id]);
  }

  const reconciled = { ...serverDrafts };
  for (const lineIds of lineIdsBySku.values()) {
    const localReceived = lineIds.reduce(
      (total, lineId) => total + countedQuantity(localDrafts[lineId]),
      0,
    );
    const serverReceived = lineIds.reduce(
      (total, lineId) => total + countedQuantity(serverDrafts[lineId]),
      0,
    );
    const localUnknown = lineIds.reduce(
      (total, lineId) => total + unknownCountedQuantity(localDrafts[lineId]),
      0,
    );
    const serverUnknown = lineIds.reduce(
      (total, lineId) => total + unknownCountedQuantity(serverDrafts[lineId]),
      0,
    );

    if (serverReceived >= localReceived && serverUnknown >= localUnknown) {
      for (const lineId of lineIds) {
        const localDraft = localDrafts[lineId];
        const serverDraft = reconciled[lineId];
        if (localDraft != null && serverDraft != null && draftHasMpnFields(localDraft) && !draftHasMpnFields(serverDraft)) {
          reconciled[lineId] = {
            ...serverDraft,
            scanned_mpn: localDraft.scanned_mpn,
            mpn_matched: localDraft.mpn_matched,
            unknown_mpn_confirmed: localDraft.unknown_mpn_confirmed,
            approved_component_id: localDraft.approved_component_id,
          };
        }
      }
      continue;
    }
    for (const lineId of lineIds) {
      if (localDrafts[lineId] != null) {
        reconciled[lineId] = localDrafts[lineId]!;
      }
    }
  }

  return reconciled;
}

export function mergeSavedInboundReceiptIntoDetail(
  detail: InboundDetailResponse,
  saved: InboundReceipt,
): InboundDetailResponse {
  const byId = detail.receipts.findIndex((receipt) => receipt.id === saved.id);
  if (byId >= 0) {
    const receipts = [...detail.receipts];
    receipts[byId] = saved;
    return { ...detail, receipts };
  }
  const withoutStaleDraft = detail.receipts.filter(
    (receipt) =>
      !(
        receipt.status === "draft"
        && receipt.id !== saved.id
        && inboundReceiptMatchesArrivalPackage(receipt, saved.inbound_arrival_package_id)
      ),
  );
  return { ...detail, receipts: [...withoutStaleDraft, saved] };
}

/**
 * Sealed bags and reels stay on the purchase-order line that closed them.
 * Duplicate SKU rows show one combined count, which can leave the sealed line's
 * draft at zero until the next quantity is keyed. Saving that zero is not a
 * clear — the sealed quantity has to stay on the line.
 */
export function inboundReceiptItemQuantitiesForSave(
  line: InboundLineRow,
  lines: readonly InboundLineRow[],
  drafts: Record<string, LineDraft>,
  closedItems: readonly ClosedSubcontainerItem[],
): { receivedQuantity: number; unknownQuantity: number } {
  const lineIds = isOrderLine(line) ? inboundLineIdsForSku(line.sku, lines) : [line.source_item_id];
  let receivedTotal = 0;
  let unknownTotal = 0;
  let closedReceived = 0;
  let closedUnknown = 0;
  const closedReceivedByLine = new Map<string, number>();
  const closedUnknownByLine = new Map<string, number>();
  for (const lineId of lineIds) {
    receivedTotal += countedQuantity(drafts[lineId]);
    unknownTotal += unknownCountedQuantity(drafts[lineId]);
    const closedAny = closedQuantityForLine(lineId, closedItems);
    const closedUnknownForLine = closedQuantityForLineSku(
      lineId,
      INBOUND_UNKNOWN_PART_LABEL_SKU,
      closedItems,
    );
    const closedKnown = Math.max(0, closedAny - closedUnknownForLine);
    closedReceived += closedKnown;
    closedUnknown += closedUnknownForLine;
    closedReceivedByLine.set(lineId, closedKnown);
    closedUnknownByLine.set(lineId, closedUnknownForLine);
  }
  const openReceived = Math.max(receivedTotal, closedReceived) - closedReceived;
  const openUnknown = Math.max(unknownTotal, closedUnknown) - closedUnknown;
  const openReceivedByLine = assignOpenQuantityToPurchaseOrderLines(lineIds, lines, openReceived);
  const openUnknownByLine = assignOpenQuantityToPurchaseOrderLines(lineIds, lines, openUnknown);
  return {
    receivedQuantity:
      (closedReceivedByLine.get(line.source_item_id) ?? 0)
      + (openReceivedByLine.get(line.source_item_id) ?? 0),
    unknownQuantity:
      (closedUnknownByLine.get(line.source_item_id) ?? 0)
      + (openUnknownByLine.get(line.source_item_id) ?? 0),
  };
}

/**
 * One finished-good tile can cover several labor lines. Fill the line that still
 * has room before the next line of that finished good. The last line keeps any overage.
 */
function assignOpenQuantityToPurchaseOrderLines(
  lineIds: readonly string[],
  lines: readonly InboundLineRow[],
  openQuantity: number,
): Map<string, number> {
  const assigned = new Map<string, number>();
  if (lineIds.length === 0) return assigned;
  const byId = new Map(lines.map((line) => [line.source_item_id, line]));
  let remaining = openQuantity;
  for (let index = 0; index < lineIds.length; index += 1) {
    const lineId = lineIds[index]!;
    const isLast = index === lineIds.length - 1;
    const line = byId.get(lineId);
    const expected = Number(line?.expected_quantity ?? 0);
    const alreadyReceived = Number(line?.already_received_quantity ?? 0);
    const room = Number.isFinite(expected)
      ? Math.max(0, expected - (Number.isFinite(alreadyReceived) ? alreadyReceived : 0))
      : remaining;
    const take = isLast ? remaining : Math.min(remaining, room);
    assigned.set(lineId, take);
    remaining -= take;
  }
  return assigned;
}

export function inboundOpenSubcontainerInputLines(
  lines: readonly InboundLineRow[],
  drafts: Record<string, LineDraft>,
  closedItems: readonly ClosedSubcontainerItem[],
): {
  source_item_id: string;
  sku: string;
  received_quantity: number;
  unknown_mpn_quantity: number;
  scanned_mpn: string | null;
}[] {
  return lines.map((line) => {
    const draft = drafts[line.source_item_id];
    if (!isOrderLine(line)) {
      return {
        source_item_id: line.source_item_id,
        sku: line.sku,
        received_quantity: countedQuantity(draft),
        unknown_mpn_quantity: unknownCountedQuantity(draft),
        scanned_mpn: draft?.scanned_mpn ?? null,
      };
    }
    const quantities = inboundReceiptItemQuantitiesForSave(line, lines, drafts, closedItems);
    const mpnDraft = resolveInboundOrderLineMpnDraft(line, lines, drafts);
    return {
      source_item_id: line.source_item_id,
      sku: line.sku,
      received_quantity: quantities.receivedQuantity,
      unknown_mpn_quantity: quantities.unknownQuantity,
      scanned_mpn: mpnDraft.scanned_mpn.trim() !== "" ? mpnDraft.scanned_mpn.trim() : null,
    };
  });
}

export function lineInputPayload(
  lines: InboundLineRow[],
  drafts: Record<string, LineDraft>,
  closedItems: readonly ClosedSubcontainerItem[] = [],
  onlySourceItemIds?: readonly string[],
): InboundReceiptItemInput[] {
  const onlyIds = onlySourceItemIds == null ? null : new Set(onlySourceItemIds);
  const orderLines = lines.filter(isOrderLine);
  const quantitiesByLineId = new Map(
    orderLines.map((line) => [
      line.source_item_id,
      inboundReceiptItemQuantitiesForSave(line, lines, drafts, closedItems),
    ]),
  );
  return orderLines.flatMap((line) => {
    if (onlyIds != null && !onlyIds.has(line.source_item_id)) return [];
    const draft = drafts[line.source_item_id] ?? EMPTY_LINE_DRAFT;
    const quantities = quantitiesByLineId.get(line.source_item_id) ?? {
      receivedQuantity: 0,
      unknownQuantity: 0,
    };
    const holdsQuantity = quantities.receivedQuantity > 0 || quantities.unknownQuantity > 0;
    if (!holdsQuantity && !inboundLineDraftNeedsReceiptPersist(line, draft)) return [];
    const lineIds = inboundLineIdsForSku(line.sku, lines);
    const skuHoldsQuantity = lineIds.some((lineId) => {
      const siblingQuantities = quantitiesByLineId.get(lineId);
      return (
        (siblingQuantities?.receivedQuantity ?? 0) > 0
        || (siblingQuantities?.unknownQuantity ?? 0) > 0
      );
    });
    const mpnDraft =
      holdsQuantity || !skuHoldsQuantity
        ? resolveInboundOrderLineMpnDraft(line, lines, drafts)
        : emptyInboundLineMpnDraftFields();
    return [{
      source_item_id: Number(line.source_item_id),
      received_quantity: quantities.receivedQuantity,
      count_method: draft.count_method,
      condition_notes: draft.condition_notes.trim() || null,
      iqc_status: draft.iqc_status,
      iqc_notes: draft.iqc_notes.trim() || null,
      iqc_passed_quantity: parseOptionalIqcQuantity(draft.iqc_passed_quantity),
      iqc_failed_quantity: parseOptionalIqcQuantity(draft.iqc_failed_quantity),
      iqc_skipped_quantity: parseOptionalIqcQuantity(draft.iqc_skipped_quantity),
      scanned_mpn: mpnDraft.scanned_mpn.trim() !== "" ? mpnDraft.scanned_mpn.trim() : null,
      manufacturer_name: mpnDraft.manufacturer_name.trim() !== "" ? mpnDraft.manufacturer_name.trim() : null,
      unknown_mpn_confirmed: mpnDraft.unknown_mpn_confirmed,
      unknown_mpn_quantity: quantities.unknownQuantity,
      approved_component_id:
        mpnDraft.approved_component_id != null && mpnDraft.approved_component_id.trim() !== ""
          ? Number.parseInt(mpnDraft.approved_component_id, 10)
          : null,
    }];
  });
}

export function parseArrivalPackageIdForSave(packageId: string | null): number | null {
  if (packageId == null || packageId.trim() === "") return null;
  const parsed = Number.parseInt(packageId, 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

export function buildReceiptSaveInput(input: {
  sourceType: InboundSourceType;
  sourceId: string;
  arrivalPackageId: string | null;
  notes: string;
  lines: InboundLineRow[];
  drafts: Record<string, LineDraft>;
  closedItems?: readonly ClosedSubcontainerItem[];
  retainEmptyDraft?: boolean;
  /** When set, only these lines are written. Other receipt lines stay as they are. */
  onlySourceItemIds?: readonly string[];
}): InboundReceiptInput {
  return {
    source_type: input.sourceType,
    source_id: Number(input.sourceId),
    inbound_arrival_package_id: parseArrivalPackageIdForSave(input.arrivalPackageId),
    carrier: null,
    notes: input.notes.trim() || null,
    items: lineInputPayload(
      input.lines,
      input.drafts,
      input.closedItems ?? [],
      input.onlySourceItemIds,
    ),
    retain_empty_draft: input.retainEmptyDraft,
  };
}

export function formatQuantityDraftDisplay(draft: string): string {
  if (draft === "") return "0";
  const parsed = Number.parseInt(draft, 10);
  if (!Number.isFinite(parsed)) return draft;
  return formatNumber(parsed);
}
